import { PageShell, GetStarted, Action } from '../components';
import { tiers as TIERS, SLOTS, FAQItem } from '../components/Pricing';
import { PageBody, PageHero, Section, FeatureRows, Note, DataTable, PaintingBand } from '../components/page';
import type { Cell } from '../components/page';
import { signupForPlan } from '../lib/appUrls';

/**
 * /pricing. The tier grid is the landing page's grid (same classes, same data,
 * same six-slot axis as src/components/Pricing.tsx), rendered here rather than
 * through <Pricing> because that component carries its own shorter FAQ and this
 * page has the fuller one: one FAQ per page.
 *
 * Mirrors platform-core/scripts/seedstatio/main.go and src/llms-intro.txt. Changing
 * a price here without changing the seed ships marketing the billing system will
 * not honour. Change Pricing.tsx in the same PR.
 */

const ENTERPRISE_MAIL = 'mailto:dans@romans.dev?subject=Statio%20Enterprise';

const COLUMNS = ['Feature', 'Free', 'Developer', 'Team', 'Business', 'Enterprise'];

const COMPARISON: Cell[][] = [
  ['MCP servers', '3', '10', '25', 'Unlimited', 'Unlimited'],
  ['Hosted from your spec', '1', '3', '10', '50', 'Custom'],
  ['Hosted from your Docker image', false, false, true, true, true],
  ['External MCPs (your URL)', true, true, true, true, true],
  ['API calls / month', '10,000', '50,000', '500,000', '2,000,000', 'Custom'],
  ['Members', '1', '1', 'Up to 15', 'Unlimited', 'Unlimited'],
  ['Encrypted credential storage', true, true, true, true, true],
  ['Catalog', 'Community', 'Full', 'Full', 'Full', 'Full'],
  ['Audit log retention', '90 days', '90 days', '90 days', '90 days', 'Up to 2 years'],
  ['Tool call retention', '30 days', '30 days', '30 days', '30 days', 'Up to 2 years'],
  ['Model call retention', '13 months', '13 months', '13 months', '13 months', '13 months'],
  ['Fleet MCP discovery', false, false, true, true, true],
  ['Per-tool allow and deny', true, true, true, true, true],
  ['Threat scanning on calls', true, true, true, true, true],
  ['Full RBAC: roles, teams and team grants', false, false, true, true, true],
  ['Overage billing (opt-in)', false, true, true, true, 'Custom'],
  ['SSO / SAML', false, false, false, true, true],
  ['SIEM export', false, false, false, true, true],
  ['Export audit logs, tool calls and model calls', false, false, false, true, true],
  ['Self-hosted in your cloud', false, false, false, false, true],
  ['Support', 'Community', 'Email', 'Email, 2 business days', 'Priority, 1 business day', 'Named contact'],
  ['SLA', false, false, false, false, '99.9%'],
];

const ENTERPRISE = [
  {
    title: 'Self-hosted in your cloud',
    body: 'We ship Statio as licensed containers you run in your own cloud; talk to us about the deployment model. Everything else is the same product: per-tool policies, threat scanning and the audit trail.',
  },
  {
    title: 'No ceilings',
    body: 'Unlimited servers and members, custom call volume, and records kept for up to two years.',
  },
  {
    title: 'Audit exports for your review',
    body: 'Audit logs, tool calls and model calls as CSV or NDJSON files, a year at a time.',
  },
  {
    title: 'SLA and a named contact',
    body: 'A 99.9% SLA and a named contact rather than a support queue.',
  },
];

const FAQS = [
  {
    q: 'Why not just run an MCP gateway myself?',
    a: 'You can. Routing is the easy part. What takes the time is everything around it: per-tool permissions checked on every call, credentials encrypted at rest and never handed to the agent, an audit trail your security team accepts, and discovering the servers developers installed without telling anyone. Statio is that layer, and it works without an EDR or MDM, which is where most self-built setups stop.',
  },
  {
    q: 'How is this different from an enterprise AI governance platform?',
    a: 'Mostly that you can start right now. Ours is self-serve with published pricing: sign up, connect a server, set a policy, no demo call and no annual contract. The enterprise platforms in this space gate everything behind sales, which works if you are 500 people and does not if you are twelve.',
  },
  {
    q: 'What counts as an MCP server?',
    a: "Each API integration (Stripe, GitHub, Slack, etc.) runs as one MCP server. If you connect three APIs, that's three MCP servers.",
  },
  {
    q: 'What counts as a hosted MCP server?',
    a: 'One that runs on our infrastructure, and there are two ways to get one. Give us an OpenAPI spec and we generate the server, build it and run it, on every plan including Free. Or, from Team up, give us a Docker image of a server you already built and we run that image as it is. Hosted servers sleep when idle. Servers you install from the catalog run on shared Statio infrastructure and do not count against the hosted allowance, and external MCPs you host yourself and connect by URL do not count either. Additional hosted servers are $9/month each.',
  },
  {
    q: 'Can I use my own OpenAPI spec or Docker image on the Free plan?',
    a: 'A spec, yes: Free hosts one server generated from your OpenAPI spec, so you get from upload to a working endpoint without a card. Docker images run from Team up. Until then, run your container yourself and connect it to Statio by URL as an external server at no cost.',
  },
  {
    q: 'Do you charge per seat?',
    a: 'No. Every plan is a flat monthly price, so adding someone to your org never changes your bill. Developer is a single-member plan, Team covers up to 15 members, and Business and Enterprise are unlimited. The cap is a plan boundary, not a meter. You are never billed per head.',
  },
  {
    q: 'What happens when I hit my call limit?',
    a: 'On Free, calls beyond 10,000/month are refused and nothing is charged. There is no way to spend money on the Free plan. On a paid plan, calls are refused too unless you have turned on overage billing.',
  },
  {
    q: 'How does overage billing work?',
    a: 'It is off by default. When you enable it you set a monthly spend cap, and calls past your included allowance bill at $30 per additional 100,000, the same rate Team and Business include. We alert you at 80% of your allowance, when overage starts, and at 80% of your cap. Hit the cap and calls are refused rather than billed further.',
  },
  {
    q: 'How long are audit logs retained?',
    a: 'Every plan keeps 90 days of audit log, 30 days of tool calls and 13 months of model calls, and you can shorten the first two in settings. Business adds file exports and SIEM streaming. Enterprise keeps records for up to two years.',
  },
  {
    q: 'Can I switch plans or cancel anytime?',
    a: 'Yes. Upgrade, downgrade, or cancel from your dashboard. Cancelling drops you to Free at the end of the period rather than deleting anything. Your servers, policies and audit history stay put, and you can resubscribe in a click.',
  },
  {
    q: 'Can I self-host?',
    a: 'Yes, on Enterprise. We ship Statio as licensed containers you run in your own cloud; talk to us about the deployment model. Everything else is the same product: per-tool policies, threat scanning and the audit trail.',
  },
  {
    q: 'Do you offer annual billing?',
    a: 'Yes. Annual plans save 17%, which is 2 months free on any paid tier.',
  },
  {
    q: 'Is my data secure?',
    a: 'Credentials are encrypted at rest with AES-256-GCM and fetched at call time. Agents never receive raw credentials. Every tool call is logged with identity, timestamp, latency and outcome. Credentials are never logged in plaintext.',
  },
];

/**
 * Same disclosure pattern as FAQItem in components/Pricing.tsx: a real button
 * inside an h3, and the answer always rendered (hidden while closed) so it
 * stays in the prerendered HTML.
 */
export default function PricingPage() {
  return (
    <PageShell>
      <PageBody>
        <PageHero
          title="Flat monthly prices, never per seat."
          lede="Free is three MCP servers, one of them hosted, and 10,000 calls a month, with no card and no way to spend money, and annual plans save 17% on every paid tier."
        />

        <Section id="plans" lede="Inside a plan, adding a member never changes your bill.">
          <div className="l-tiers">
            {TIERS.map((tier) => (
              <article key={tier.name} className={tier.highlight ? 'l-tier hi' : 'l-tier'}>
                <h3>{tier.name}</h3>
                <div className="price">
                  {tier.price}
                  <small>{tier.period}</small>
                </div>
                <p className="desc">{tier.description}</p>
                <ul>
                  {tier.features.map((f, slot) => (
                    <li key={SLOTS[slot]}>
                      <span className="k">{SLOTS[slot]}</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Action
                  href={signupForPlan(tier.name)}
                  external
                  className={tier.highlight ? 's-btn s-btn-pri' : 's-btn s-btn-sec'}
                >
                  {tier.cta}
                </Action>
              </article>
            ))}
            {/* Enterprise sits in the grid with a published floor, not a self-serve price. */}
            <article className="l-tier">
              <h3>Enterprise</h3>
              <div className="price">
                from $15K<small>/yr</small>
              </div>
              <p className="desc">Self-hosted in your cloud.</p>
              <ul>
                <li>Unlimited servers and members</li>
                <li>Custom call volume, records kept up to two years</li>
                <li>99.9% SLA, named contact</li>
                <li>Audit exports for your review</li>
              </ul>
              <Action href={ENTERPRISE_MAIL} className="s-btn s-btn-sec">
                Talk to us
              </Action>
            </article>
          </div>
          {/* Same three facts as the landing page fine print, in the same order. */}
          <div className="l-fineprint">
            <p>
              <b>Annual billing.</b> Pay for 10 months, get 12, a 17% saving on every paid tier.
            </p>
            <p>
              <b>Extra hosted servers.</b> $9/month each, beyond the number your tier includes.
            </p>
            <p>
              <b>Overage is off.</b> Go past your monthly API calls and the extra calls are refused, at no charge, until
              you turn overage on yourself. If you do, you set a monthly spend cap first, and the calls past your
              allowance bill at $30 per 100,000.
            </p>
          </div>
        </Section>

        <Section
          id="compare"
          title="Compare plans"
          lede="Per-tool allow and deny and threat scanning are on every plan."
        >
          <DataTable caption="Statio plan comparison" columns={COLUMNS} rows={COMPARISON} highlight={3} />
          <Note title="What export means">
            Admins download audit logs, tool calls and model calls as CSV or NDJSON for a compliance review. Statio has
            no completed compliance certification, and we will not imply one.
          </Note>
        </Section>

        <Section id="enterprise" title="Enterprise" lede="From $15K a year, self-hosted in your cloud." split>
          <FeatureRows items={ENTERPRISE} columns={1} />
        </Section>

        <Section id="faq" title="Frequently asked questions">
          <div>
            {FAQS.map((faq, index) => (
              <FAQItem key={faq.q} id={String(index)} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </Section>

        <PaintingBand
          art="crescenza"
          title="Need it inside your own network?"
          body="Enterprise runs as containers in your cloud, from $15K a year."
          action={
            <Action href={ENTERPRISE_MAIL} className="s-btn">
              Talk to us
            </Action>
          }
        />
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}
